import { Platform } from 'react-native';

export type ChatScreenPalette = {
  background: string;
  edgeTransparent: string;
  headerFallbackBg: string;
  // Figma node 3062:6150: header glass circles + upgrade pill: #D7E6F5 @ 25%
  // tint with a hairline rgba(0,0,0,0.06) border and #4A4A4D icon strokes.
  headerCircleTint: string;
  headerCircleBorder: string;
  headerIconColor: string;
  /** Hairline ring around the account avatar in the nav bar. */
  headerAvatarRing: string;
  upgradeTextColor: string;
  upgradeAccent: string;
  suggestionChipBg: string;
  composerFallbackBg: string;
  secondaryText: string;
  primaryRoundFill: string;
  primaryRoundIcon: string;
};

export const CHAT_INPUT_NATIVE_ID = 'chat-composer-input';

/**
 * Monospace face for figures that must not jitter as they change — the live
 * elapsed timer, task-row metas, search queries in a trace row. Mirrors the
 * web's `--font-mono` stack.
 */
export const CHAT_MONO_FONT = Platform.select({
  ios: 'Menlo',
  android: 'monospace',
  default: 'monospace',
});

// Composer pill border: Figma node 3062:6080 vertical gradient stroke
// (#B4A6F9 → #815EF6 → #8162F5 → #FE97E9), 3px, top → bottom.
export const COMPOSER_BORDER_GRADIENT = {
  colors: ['#B4A6F9', '#815EF6', '#8162F5', '#FE97E9'] as const,
  locations: [0, 0.346, 0.5625, 1] as const,
  start: { x: 0.5, y: 0 },
  end: { x: 0.5, y: 1 },
};

const LIGHT_PALETTE: ChatScreenPalette = {
  background: '#FFFFFF',
  edgeTransparent: 'rgba(255,255,255,0)',
  headerFallbackBg: 'rgba(247,250,253,0.7)',
  headerCircleTint: 'rgba(215,230,245,0.25)',
  headerCircleBorder: 'rgba(0,0,0,0.12)',
  headerIconColor: '#4A4A4D',
  headerAvatarRing: 'rgba(0,0,0,0.10)',
  upgradeTextColor: '#7D7C81',
  upgradeAccent: '#5E5CE6',
  suggestionChipBg: 'rgba(239,239,241,0.75)',
  composerFallbackBg: '#FFFFFF',
  secondaryText: '#8E8E93',
  primaryRoundFill: '#000000',
  primaryRoundIcon: '#FFFFFF',
};

const DARK_PALETTE: ChatScreenPalette = {
  background: '#000000',
  edgeTransparent: 'rgba(0,0,0,0)',
  headerFallbackBg: 'rgba(24,24,24,0.88)',
  headerCircleTint: 'rgba(215,230,245,0.10)',
  headerCircleBorder: 'rgba(255,255,255,0.06)',
  headerIconColor: '#C7C7CC',
  headerAvatarRing: 'rgba(255,255,255,0.14)',
  upgradeTextColor: '#C7C7CC',
  upgradeAccent: '#5E5CE6',
  suggestionChipBg: '#141414',
  composerFallbackBg: '#1C1C1E',
  secondaryText: '#7D7D82',
  primaryRoundFill: '#FFFFFF',
  primaryRoundIcon: '#000000',
};

export const CHAT_SCREEN_SPEC = {
  header: {
    iosTopPadding: 54,
    androidTopPadding: 44,
    horizontalPadding: 16,
    bottomPadding: 8,
    itemGap: 8,
    // Figma node 3062:6150: 50px circles, 18px icon glyphs, 50px-tall upgrade pill.
    circleSize: 50,
    iconSize: 18,
    upgradeHeight: 50,
    upgradeHorizontalPadding: 14,
    upgradeGap: 4,
    upgradeIconSize: 18,
    upgradeTextSize: 12,
    // Account avatar in the trailing nav-bar slot (mock 110): a 36pt circle with
    // an unread dot on its top-end corner when Inbox or Notifications have news.
    avatarSize: 36,
    avatarUnreadDotSize: 9,
    avatarUnreadDotBorder: 2,
  },
  // Empty-thread home (mock 110): the sparkle + greeting float in the free
  // space, the suggestion rows hug the composer.
  home: {
    sparkleSize: 44,
    sparkleGap: 18,
    greetingSize: 23,
    greetingLineHeight: 29,
    greetingLetterSpacing: -0.3,
    greetingMaxWidth: 400,
    greetingHorizontalPadding: 24,
    rowHeight: 44,
    rowHorizontalPadding: 24,
    rowGap: 8,
    rowIconSize: 16,
    rowLabelSize: 16,
    rowPressedOpacity: 0.6,
    listBottomGap: 18,
  },
  suggestions: {
    radius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minWidth: 120,
    containerHorizontalPadding: 12,
    containerGap: 8,
    titleSize: 14,
    subtitleSize: 13,
    pressedOpacity: 0.92,
  },
  composer: {
    wrapperHorizontalPadding: 12,
    wrapperBottomGap: 8,
    contentBottomClearance: Platform.OS === 'android' ? 96 : 50,
    jumpButtonSize: 36,
    jumpButtonSpacing: 10,
    // Figma node 3062:6080: white pill, 3px gradient border, black mic
    // button, and "+" attach button. Keep it roomy enough for input.
    radius: 34,
    borderWidth: 3,
    minHeight: 68,
    // The input row is the pill's only height source at rest: it must equal
    // minHeight − borderWidth × 2 so the 68px pill has zero dead space and its
    // children (plus / input / mic) center on the true middle of the pill.
    rowMinHeight: 62,
    rowHorizontalPadding: 10,
    rowGap: 8,
    plusTapSize: 32,
    plusIconSize: 14,
    smallMicTapSize: 22,
    primaryActionSize: 40,
    iconSize: 20,
    micIconSize: 18,
    placeholderSize: 16,
    inputLineHeight: 24,
    inputVerticalPadding: 10,
    maxVisibleLines: 4,
  },
  androidEdgeFade: {
    topExtension: 48,
    bottomExtension: 72,
  },
} as const;

export function getChatScreenPalette(isDark: boolean): ChatScreenPalette {
  return isDark ? DARK_PALETTE : LIGHT_PALETTE;
}

/**
 * Geometry for the assistant "activity trace" — the vertical rail of live steps
 * (Thinking… / Searching jobs… / Found 12 matches) that replaced the per-message
 * avatar header, the `Tabbio · <tool>` label and the lone pulsing dot.
 *
 * Both platforms read these numbers: native via `CHAT_RESPONSE_SPEC`, web via the
 * `--chat-*` custom properties mirrored in `global.css`. Keeping one source means
 * a step rail can never drift between iOS and the browser.
 */
export const CHAT_RESPONSE_SPEC = {
  trace: {
    /** Width of the icon gutter. Cards and text align to its trailing edge. */
    gutter: 22,
    iconSize: 14,
    /** Vertical space between two steps. */
    stepGap: 6,
    /** Space between the trace and the body text that follows it. */
    trailingGap: 10,
    labelSize: 13,
    labelLineHeight: 18,
    /** Collapsed summary row ("3 steps · Read CV, searched jobs"). */
    summarySize: 13,
    /** Indent applied to a card that hangs off its own step. */
    cardIndent: 0,
  },
  /**
   * The status surfaces that narrate a turn: the Nova loader + shimmer label
   * + elapsed timer, and the trace header that replaces it once steps exist.
   */
  status: {
    labelSize: 13,
    labelLineHeight: 18,
    elapsedSize: 12,
    headerIconSize: 16,
    chevronSize: 14,
    /** One trace row: glyph slot, primary label, optional secondary meta. */
    rowMinHeight: 28,
    rowGap: 4,
    rowGlyphSize: 14,
    rowSpinnerSize: 12,
    primarySize: 13,
    secondarySize: 11.5,
    /** The hairline rail the rows hang off, offset from the header glyph. */
    railInset: 5,
    railIndent: 16,
    /** Stagger between consecutive rows entering. */
    rowStaggerMs: 120,
  },
  identity: {
    /** Agent mark beside the copy/regenerate/feedback row. */
    markSize: 18,
    nameSize: 12,
    gap: 6,
  },
  /** Nova loader: the mascot beside a shimmering status label. */
  loader: {
    iconSize: 20,
    labelSize: 13,
    elapsedSize: 12,
    gap: 8,
  },
  actions: {
    iconSize: 15,
    /** 28px controls plus 8px native hit slop reach a 44px target. */
    tapSize: 28,
    hitSlop: 8,
    gap: 2,
    topGap: 8,
  },
  /** Task rows: applications, Nova runs — one unit of work per row. */
  tasks: {
    rowHeight: 44,
    paddingHorizontal: 10,
    gap: 10,
    badgeSlot: 24,
    ringSize: 24,
    ringStroke: 2,
    badgeSize: 22,
    labelSize: 13,
    amountSize: 12.5,
    pillHeight: 22,
    pillSize: 11.5,
    chevronSize: 15,
    capsuleRadius: 22,
    capsuleOpenRadius: 14,
    detailLabelSize: 12,
    detailMetaSize: 11.5,
  },
} as const;

/**
 * The single geometry contract for a tool card surface.
 *
 * `CardShell` and the loading `ShimmerCard` both consume this. Before it existed
 * they disagreed (480 vs 520 wide, radius 16 vs 24, bordered vs borderless), so
 * every tool call visibly snapped wider and rounder the instant its result
 * landed. A skeleton must occupy exactly the box its result will occupy.
 */
/**
 * Surface roles for tool cards, generated images and the artifact viewer,
 * mapped from Gemini's surface-container scale onto Tabbio's neutral palette.
 * The web twin is the `--tabbio-surface-container*`, `--tabbio-image-*` and
 * `--tabbio-lightbox` block in global.css; keep the two in step.
 */
export type ArtifactPalette = {
  surfaceContainer: string;
  surfaceContainerLow: string;
  surfaceContainerLowest: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;
  imageOverlay: string;
  lightbox: string;
  /** Icons and text on the lightbox (the on-surface-variant role). */
  onLightbox: string;
  /** The three steps of the generated-image loading gradient. */
  imageLoading: readonly [string, string, string];
};

const ARTIFACT_PALETTE_LIGHT: ArtifactPalette = {
  surfaceContainer: '#f3f3f3',
  surfaceContainerLow: '#f9f9f9',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerHigh: '#ededed',
  surfaceContainerHighest: '#dfdfdf',
  imageOverlay: 'rgba(255,255,255,0.75)',
  lightbox: '#ffffff',
  onLightbox: '#444746',
  imageLoading: ['rgba(243,243,243,0.2)', '#f3f3f3', '#dfdfdf'],
};

const ARTIFACT_PALETTE_DARK: ArtifactPalette = {
  surfaceContainer: '#1e1f20',
  surfaceContainerLow: '#1b1c1d',
  surfaceContainerLowest: '#0d0d0d',
  surfaceContainerHigh: '#2d2e31',
  surfaceContainerHighest: '#333537',
  imageOverlay: 'rgba(45,46,49,0.75)',
  lightbox: '#000000',
  onLightbox: '#c4c7c5',
  imageLoading: ['rgba(19,19,20,0.2)', '#1e1f20', '#333537'],
};

export function getArtifactPalette(isDark: boolean): ArtifactPalette {
  return isDark ? ARTIFACT_PALETTE_DARK : ARTIFACT_PALETTE_LIGHT;
}

/**
 * The tool-card fill: the surface-container role, flat, on every platform.
 * Opaque rather than an alpha tint so the card reads the same over the web's
 * cream page and the phone's black chat background.
 */
export function getCardSurface(isDark: boolean): string {
  return getArtifactPalette(isDark).surfaceContainer;
}

export const CARD_SHELL_SPEC = {
  radius: 16,
  maxWidth: 520,
  paddingHorizontal: 16,
  paddingVertical: 16,
  rowGap: 10,
  /** Row inside a list card: leading visual, title, 2-line subtitle, trailing. */
  row: {
    minHeight: 56,
    gap: 12,
    leadingSize: 34,
    titleSize: 14,
    subtitleSize: 13,
    subtitleLineHeight: 18,
    subtitleLines: 2,
  },
  header: {
    titleSize: 16,
    titleLineHeight: 24,
    metaSize: 12,
    gap: 8,
    bottomGap: 8,
  },
  footer: {
    height: 40,
    labelSize: 13,
  },
} as const;

/**
 * Chat-local motion. Durations come from `motion.duration` in `@tabbio/tokens`
 * so the chat surface stops hand-rolling its own timings; the two values that
 * have no token equivalent (the shimmer sweep and the step-settle spring) are
 * defined here because they are specific to this surface.
 */
export const CHAT_MOTION = {
  /** Status label crossfade when a step changes tense. */
  statusCrossfadeMs: 200,
  /** A newly arrived step fades + rises into place. */
  stepEnterMs: 220,
  stepEnterTranslateY: 4,
  /** One full left→right pass of the shimmer highlight. */
  shimmerSweepMs: 1400,
  /** Trace expand/collapse. */
  collapseMs: 240,
  /** Trace body / task detail disclosure, mirrors the web's 400ms / 300ms. */
  disclosureMs: 400,
  taskDisclosureMs: 300,
  /** A trace row or follow-up rising into place. */
  rowEnterMs: 320,
  rowEnterTranslateY: 4,
  /** Actions, sources and follow-ups fading in once the answer settles. */
  settleFadeMs: 400,
  /** A streamed word resolving in. */
  wordRevealMs: 360,
} as const;

/**
 * The muted tone family shared by the trace, sources and task rows — the
 * native twin of the `--agent-tone-*` custom properties in global.css. Soft
 * variants are the same hues at low alpha, for pills.
 */
export type AgentTones = {
  accent: string;
  orange: string;
  green: string;
  red: string;
  greenSoft: string;
  redSoft: string;
};

const LIGHT_TONES: AgentTones = {
  accent: '#635b96',
  orange: '#a56b36',
  green: '#47826a',
  red: '#b45c65',
  greenSoft: 'rgba(71,130,106,0.14)',
  redSoft: 'rgba(180,92,101,0.14)',
};

const DARK_TONES: AgentTones = {
  accent: '#726bb0',
  orange: '#b98148',
  green: '#65a288',
  red: '#d27d86',
  greenSoft: 'rgba(101,162,136,0.18)',
  redSoft: 'rgba(210,125,134,0.18)',
};

export function getAgentTones(isDark: boolean): AgentTones {
  return isDark ? DARK_TONES : LIGHT_TONES;
}
